import _ from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
/* Custom React Hook that will determine whether a component is mounted. */
var useIsAppMounted = function () {
    var isMountedRef = useRef(false);
    // Using `React.useCallBack` to memoize the callback function, so that isMounted
    // will not be re-created again after each render.
    var isMounted = useCallback(function () { return isMountedRef.current; }, []);
    useEffect(function () {
        // Set the isMountedRef to true after the component is mounted
        isMountedRef.current = true;
        // Clean up action: set the isMountedRef to false when the component is unmounted
        return function () {
            isMountedRef.current = false;
        };
    }, []);
    return isMounted;
};
/* Custom React Hook that helps with cancelling previous HTTP request using AbortController */
var useRequestSignal = function () {
    var controller = useRef(null);
    var newRequestSignal = useCallback(function () {
        // Cancel the previous HTTP request if any.
        if (controller.current) {
            controller.current.abort();
        }
        controller.current = new AbortController();
        return controller.current.signal;
    }, []);
    return newRequestSignal;
};
// Custom hook to scroll to hash anchor links
// Example link: /a/addons/#microbiome-kit
// Scroll to the element with id=microbiome-kit on the account addons page
// Original from:
// eslint-disable-next-line max-len
// https://dev.to/alejomartinez8/how-to-load-a-hash-fragment-to-an-anchor-name-in-react-especially-in-first-loading-g3i
var useHashAnchorLink = function (offset) {
    if (offset === void 0) { offset = 100; }
    useEffect(function () {
        var scrollToHashElement = function () {
            var hash = window.location.hash;
            if (hash) {
                var elementToScroll = document.getElementById(hash.replace('#', ''));
                if (elementToScroll) {
                    window.scrollTo({
                        top: elementToScroll.offsetTop - offset,
                        behavior: 'smooth',
                    });
                }
            }
        };
        scrollToHashElement();
        window.addEventListener('hashchange', scrollToHashElement);
        return function () { return window.removeEventListener('hashchange', scrollToHashElement); };
    }, []);
};
var useHasStateChanged = function (resetKey, state) {
    var initialState = useRef(state);
    var initialKey = useRef(resetKey);
    var _a = useState(false), hasChanged = _a[0], setHasChanged = _a[1];
    useEffect(function () {
        setHasChanged(!_.isEqual(state, initialState.current)); // Compare state with initialState
    }, [state]);
    useEffect(function () {
        if (resetKey !== initialKey.current) {
            initialState.current = state; // Reset the initial state
            initialKey.current = resetKey; // Reset the initial key
            setHasChanged(false); // Reset the hasChanged state
        }
    }, [resetKey]);
    return hasChanged;
};
export { useIsAppMounted, useRequestSignal, useHashAnchorLink, useHasStateChanged };
